import React, { FunctionComponent } from 'react';
import withStyles from 'react-jss';
import { Grid } from 'semantic-ui-react';
import { withRouter } from 'react-router';
import { compose } from 'recompose';
import { Location } from 'history';
import queryString from 'query-string';
import {
  FlexibleWidthXYPlot,
  HorizontalGridLines,
  VerticalGridLines,
  XAxis,
  YAxis,
  VerticalBarSeries,
  LineMarkSeries,
  DiscreteColorLegend,
} from 'react-vis';

import { ClassProp } from 'helpers/types';
import Query from 'scenes/Report/components/Query';
import ReportBase, { ReportParams } from 'scenes/Report/components/ReportBase';
import { getTickFormat, Timeslice } from 'scenes/Report/Report.helper';
import WithHint, { HintArgs } from 'scenes/Report/components/WithHint';
import { theme } from 'config';

import GET_TRANSACTION_VOLUME from './TransactionVolume.queries';
import { scalingData, tickScalingData } from '../PerformanceReports.helper';
import {
  barTransformers,
  lineTransformers,
  legendItems,
} from './TransactionVolume.helper';

const styles = {
  legend: {
    textAlign: 'center !important',
    marginTop: '3em !important',
  },
};

type Props = {
  classes: ClassProp<typeof styles>;
  location: Location;
  height?: number;
};

type TransactionSummary = {
  transactionDate: Date;
  numberOfTransactions: number;
  totalTransactionAmount: number;
};

type QueryResponse = {
  transactionSummary: { result?: TransactionSummary[]; timeslice?: Timeslice };
};

const tickFormat = (timeslice: Timeslice) => (value: string) => {
  const { top, bottom } = getTickFormat(timeslice, value);

  return (
    <tspan>
      <tspan x="0" dy="1em">
        {top}
      </tspan>

      <br />

      <tspan x="0" dy="1em">
        {bottom}
      </tspan>
    </tspan>
  );
};

const renderReport = ({ classes, height = 450 }: Props) => (
  { transactionSummary: { result, timeslice } }: QueryResponse = {
    transactionSummary: {},
  },
  params: ReportParams,
) => {
  const barData = barTransformers(result);
  const lineData = lineTransformers(result);

  return (
    <ReportBase
      batchKey="transactionVolume"
      header="Transaction Volume"
      params={params}
    >
      <WithHint data={{ lineData }}>
        {({ onValueMouseOver, onValueMouseOut, renderHint }: HintArgs) => (
          <Grid.Row centered>
            <Grid.Column textAlign="center" width={14}>
              <FlexibleWidthXYPlot xType="ordinal" height={height}>
                <VerticalGridLines />
                <HorizontalGridLines />
                <XAxis tickFormat={tickFormat(timeslice as Timeslice)} />

                <YAxis
                  orientation="right"
                  tickFormat={tickScalingData(barData)}
                  title="Cashback Amount"
                />

                <YAxis
                  orientation="left"
                  tickFormat={tickScalingData(lineData)}
                  title="Total Transactions Amount"
                />

                <VerticalBarSeries
                  color={theme.violet}
                  data={scalingData(barData)}
                  stack
                />

                <LineMarkSeries
                  onValueMouseOver={onValueMouseOver('lineData')}
                  onValueMouseOut={onValueMouseOut}
                  color={theme.seafoamGreen}
                  data={scalingData(lineData)}
                />

                {renderHint()}
              </FlexibleWidthXYPlot>
            </Grid.Column>

            <Grid.Row centered>
              <DiscreteColorLegend
                className={classes.legend}
                orientation="horizontal"
                items={legendItems}
              />
            </Grid.Row>
          </Grid.Row>
        )}
      </WithHint>
    </ReportBase>
  );
};

const TransactionVolume: FunctionComponent<Props> = (props) => {
  const params = queryString.parse(props.location.search) as ReportParams;

  return (
    <Query
      query={GET_TRANSACTION_VOLUME}
      queryKey="result"
      reportName="Transaction Volume"
      batchKey="transactionVolume"
      variables={{ params }}
      render={renderReport(props)}
    />
  );
};

export default compose<Props, any>(
  withStyles(styles),
  withRouter,
)(TransactionVolume);
